import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import createCarbonApi from 'api/carbonOffset';
import createOrderApi from 'api/order';
import { currentUserSelector } from 'modules/user/userSlice';
import { showToast } from 'modules/app/toasterSlice';
import { AppThunk } from 'store';
import { syncAttribution } from 'modules/user/attributionSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { setTag, tags } from 'utils/tags';

interface OffsetPrice {
  price: number;
  currency: string;
  vat?: number;
}

interface OrderPayload {
  number: string;
  date: string;
}

interface State {
  amount?: number;
  isLoading: boolean;
  price?: number;
  currency?: string;
  orderNumber?: string;
  orderDate?: string;
  vat?: number;
}

const initialState: State = {
  isLoading: false,
};

const slice = createSlice({
  name: 'offsetCarbonPersonal',
  initialState,
  reducers: {
    reset: (): State => {
      return { ...initialState };
    },
    setPrice: (state: State, action: PayloadAction<OffsetPrice>): State => {
      state.price = action.payload.price;
      state.currency = action.payload.currency;
      state.vat = action.payload.vat;
      return state;
    },
    setAmount: (state: State, action: PayloadAction<number>): State => {
      state.amount = action.payload;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setOrder: (state: State, action: PayloadAction<OrderPayload>): State => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      return state;
    },
  },
});

const { setPrice, reset, setLoading, setOrder, setAmount } = slice.actions;
export { reset };
export default slice.reducer;

export const getOffsetPrice = (kilosCo2: number): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const data = await carbonApi.getCarbonOffsetPrice(
      { kilosCo2, bulkCo2: true, percentageBulkCo2: true },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );
    dispatch(
      setPrice({
        price: data.salesPrice,
        currency: data.salesCurrency,
        vat: data.vat,
      }),
    );
    dispatch(setAmount(kilosCo2));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
  }
};

export const createOrder = (errorCallback: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const { carbonOffsetPersonal, user } = getState();
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const totalKilosCo2 = carbonOffsetPersonal.amount ?? 0;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const orderResult = await orderApi.create(
      {
        currency: carbonOffsetPersonal.currency ?? '',
        comment: '',
        description: '',
        category: 'Carbon',
        items: [
          {
            type: 'impact',
            impact: 'Co2',
            kilosCo2: totalKilosCo2,
            price: carbonOffsetPersonal.price ?? 0,
            portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
            feeFactors: {
              bulkCo2: true,
              percentageBulkCo2: true,
            },
          },
        ],
        payment: {
          type: 'invoice',
        },
        metadata: {
          order: {
            channel: 'mychooose-fulloffset',
          },
        },
      },
      user.currentAccount?.customerId ?? '',
    );
    setTag({
      ...tags.portalOffset.placeOrder,
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Bulk offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'bulk-offset',
      })),
    });
    dispatch(setLoading(false));
    dispatch(
      setOrder({
        number: orderResult.order.choooseId,
        date: orderResult.order.createdDate,
      }),
    );
    dispatch(syncAttribution());
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'common:errorTitle', descriptioni18nKey: 'common:errorDescription' }));
    errorCallback();
  }
};
